import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { POSITIONS, FOOT_OPTIONS, SHOE_SIZES, MEDICAL_COVERAGES, NATIONALITIES, COMMUNITY_PASSPORT_OPTIONS, resolveOtherValue, isCustomValue } from '../constants/playerFields';
import SelectWithAdd from '../components/SelectWithAdd';
import Layout from '@/components/Layout';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Calendar, Camera, User, Phone, Mail, MapPin, Ruler, Weight, Footprints, Clock, GraduationCap, Trophy, Home, FileText, AlertCircle, CheckCircle, XCircle, List, Grid, Download, Trash2, Eye, Heart, Activity, Dumbbell, UserCheck, Loader2, X, ZoomIn, Edit, TrendingUp, BarChart3, UserPlus, Shield, DollarSign, ChevronDown, ChevronUp, ClipboardList } from 'lucide-react';
import CircularProgress from '@/components/CircularProgress';
import ScoreBadge from '@/components/ScoreBadge';
import ImageUpload from '@/components/ImageUpload';
import DocumentUpload from '@/components/DocumentUpload';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
const API = `${BACKEND_URL}/api`;

const PlayerDetail = () => {
  const { id } = useParams();
  const { isScout, isLegales, user } = useAuth();
  const navigate = useNavigate();
  usePageTitle('Detalle de Jugador');
  const [player, setPlayer] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newNoteCategory, setNewNoteCategory] = useState('');
  const [editedPlayer, setEditedPlayer] = useState({});
  // Valores custom para campos "Otro/Otra" en modo edición
  const [editCustomValues, setEditCustomValues] = useState({ nationality: '', medical_coverage: '' });
  const handleEditCustomChange = (field, text) => setEditCustomValues(prev => ({ ...prev, [field]: text }));
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  
  // Document management states
  const [documentView, setDocumentView] = useState('list'); // 'list' or 'preview'
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null); // For editing existing documents
  const [newDocument, setNewDocument] = useState({
    type: 'contract_representation',
    name: '',
    file: '',
    metadata: { 
      expiry_date: '', 
      notes: '',
      start_date: '',
      end_date: '',
      club_name: '',
      representative_name: ''
    }
  });
  
  // Tracking history states (seguimientos del jugador)
  const [trackingData, setTrackingData] = useState([]);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [trackingFilter, setTrackingFilter] = useState('all'); // 'all', 'partido', 'entrenamiento'
  
  // Scouts list for assignment
  const [scouts, setScouts] = useState([]);

  useEffect(() => {
    fetchPlayer();
    fetchCategories();
    fetchScouts();
  }, [id]);

  const fetchScouts = async () => {
    try {
      const res = await axios.get(`${API}/auth/users`);
      const users = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setScouts(users.filter((u) => u.role === 'scout' && (u.status === 'active' || u.is_active !== false)));
    } catch (e) {
      console.warn('[fetchScouts]', e.response?.data || e.message);
    }
  };

  const fetchPlayer = async () => {
    try {
      const response = await axios.get(`${API}/players/${id}`);
      const data = response.data;
      setPlayer(data);
      setEditedPlayer(data);
      // Pre-cargar custom values si el valor guardado no está en la lista estándar
      setEditCustomValues({
        nationality: isCustomValue(data.nationality, NATIONALITIES) ? data.nationality : '',
        medical_coverage: isCustomValue(data.medical_coverage, MEDICAL_COVERAGES) ? data.medical_coverage : '',
      });
      // Si el valor es custom, mostrar "Otro/Otra" en el select
      if (isCustomValue(data.nationality, NATIONALITIES) && data.nationality) {
        setEditedPlayer(prev => ({ ...prev, nationality: 'Otro' }));
      }
      if (isCustomValue(data.medical_coverage, MEDICAL_COVERAGES) && data.medical_coverage) {
        setEditedPlayer(prev => ({ ...prev, medical_coverage: 'Otra' }));
      }
    } catch (error) {
      console.error('Error fetching player:', error);
      toast.error('Error al cargar jugador');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTrackingHistory = async (filter = 'all') => {
    setLoadingTracking(true);
    try {
      const params = { player_id: id, limit: 100 };
      if (filter !== 'all') {
        params.event_type = filter;
      }
      const response = await axios.get(`${API}/tracking`, { params });
      setTrackingData(response.data || []);
    } catch (error) {
      console.error('Error fetching tracking:', error);
      setTrackingData([]);
    } finally {
      setLoadingTracking(false);
    }
  };

  // Load tracking when filter changes
  useEffect(() => {
    if (id) {
      fetchTrackingHistory(trackingFilter);
    }
  }, [id, trackingFilter]);

  const handleSave = async () => {
    try {
      // Resolver valores "Otro/Otra" antes de guardar
      const resolvedEdited = {
        ...editedPlayer,
        nationality: resolveOtherValue(editedPlayer.nationality, editCustomValues.nationality),
        medical_coverage: resolveOtherValue(editedPlayer.medical_coverage, editCustomValues.medical_coverage),
      };

      // Build update data with only changed fields
      const updateData = {};
      
      // Basic fields - always include if edited
      const basicFields = ['first_name', 'last_name', 'birth_date', 'document', 'category_id', 
        'phone', 'emergency_phone', 'emergency_contact_name', 'medical_coverage', 'email', 'address', 'latitude', 'longitude', 'position', 'position_secondary',
        'status', 'preferred_foot', 'origin_club', 'join_date', 'educational_institution', 'academic_performance',
        'health_certificate_expiry', 'fitness_certificate_expiry', 'photo_url',
        'passport', 'community_passport', 'nationality', 'social_media',
        'mother_name', 'mother_phone', 'father_name', 'father_phone', 'tutor_name', 'tutor_phone',
        'shoe_size', 'club_trajectory', 'scouted_by'];
      
      basicFields.forEach(field => {
        if (resolvedEdited[field] !== player[field]) {
          updateData[field] = resolvedEdited[field];
        }
      });
      
      // Handle category_ids array separately (compare arrays properly)
      const editedCategoryIds = editedPlayer.category_ids || [];
      const originalCategoryIds = player.category_ids || [];
      const categoryIdsChanged = 
        editedCategoryIds.length !== originalCategoryIds.length ||
        editedCategoryIds.some((id, idx) => id !== originalCategoryIds[idx]);
      
      if (categoryIdsChanged && editedCategoryIds.length > 0) {
        updateData.category_ids = editedCategoryIds;
        // Also update category_id to first category for backwards compatibility
        updateData.category_id = editedCategoryIds[0];
      }
      
      // Numeric fields with proper parsing - only if changed
      const numericFields = [
        { key: 'height', parser: parseFloat },
        { key: 'weight', parser: parseFloat },
        { key: 'years_experience', parser: parseInt }
      ];
      
      numericFields.forEach(({ key, parser }) => {
        const editedVal = editedPlayer[key] ? parser(editedPlayer[key]) : null;
        const originalVal = player[key] || null;
        if (editedVal !== originalVal) {
          updateData[key] = editedVal;
        }
      });
      
      // Evaluation fields - ONLY include if explicitly changed by user
      // This prevents the bug where editing any field triggers overall_score recalculation
      const evaluationFields = ['technical_level', 'tactical_level', 'physical_level', 
        'psychological_level', 'relationship_with_teammates'];
      
      evaluationFields.forEach(field => {
        const editedVal = editedPlayer[field] ? parseInt(editedPlayer[field]) : null;
        const originalVal = player[field] || null;
        // Only include if the value actually changed
        if (editedVal !== originalVal) {
          updateData[field] = editedVal;
        }
      });
      
      // Only make API call if there are actual changes
      if (Object.keys(updateData).length === 0) {
        toast.info('No hay cambios para guardar');
        setEditing(false);
        return;
      }
      
      await axios.put(`${API}/players/${id}`, updateData);
      toast.success('Jugador actualizado exitosamente');
      setEditing(false);
      fetchPlayer();
    } catch (error) {
      console.error('Error updating player:', error);
      toast.error('Error al actualizar jugador');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      const noteData = { 
        note: newNote,
        category_id: newNoteCategory || (player.category_ids?.[0] || player.category_id)
      };
      await axios.post(`${API}/players/${id}/notes`, noteData);
      toast.success('Nota agregada exitosamente');
      setNewNote('');
      setNewNoteCategory('');
      fetchPlayer();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Error al agregar nota');
    }
  };

  // Document management functions
  const handleAddDocument = async () => {
    if (!newDocument.name || !newDocument.file) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    
    // Validate contract specific fields
    if (newDocument.type === 'contract_club' && !newDocument.metadata.club_name) {
      toast.error('Por favor ingresa el nombre del club');
      return;
    }
    if (newDocument.type === 'contract_representation' && !newDocument.metadata.representative_name) {
      toast.error('Por favor ingresa el nombre del representante');
      return;
    }
    if ((newDocument.type === 'contract_club' || newDocument.type === 'contract_representation') && !newDocument.metadata.end_date) {
      toast.error('Por favor ingresa la fecha de fin del contrato');
      return;
    }
    
    try {
      await axios.post(`${API}/players/${id}/documents`, newDocument);
      toast.success('Documento agregado exitosamente');
      setShowAddDocument(false);
      setNewDocument({
        type: 'contract_representation',
        name: '',
        file: '',
        metadata: { 
          expiry_date: '', 
          notes: '',
          start_date: '',
          end_date: '',
          club_name: '',
          representative_name: ''
        }
      });
      fetchPlayer();
    } catch (error) {
      console.error('Error adding document:', error);
      toast.error('Error al agregar documento');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;
    
    try {
      await axios.delete(`${API}/players/${id}/documents/${documentId}`);
      toast.success('Documento eliminado exitosamente');
      fetchPlayer();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Error al eliminar documento');
    }
  };

  const handleEditDocument = (doc) => {
    setEditingDocument({
      ...doc,
      metadata: doc.metadata || { expiry_date: '', notes: '' }
    });
  };

  const handleUpdateDocument = async () => {
    if (!editingDocument) return;
    
    try {
      const updateData = {
        type: editingDocument.type,
        name: editingDocument.name,
        metadata: editingDocument.metadata,
      };
      
      // If there's a new file, include it (and update file_name)
      if (editingDocument.newFile) {
        updateData.file = editingDocument.newFile;
        if (editingDocument.newFileName) {
          updateData.file_name = editingDocument.newFileName;
        }
      }
      
      await axios.put(`${API}/players/${id}/documents/${editingDocument.id}`, updateData);
      toast.success('Documento actualizado exitosamente');
      setEditingDocument(null);
      fetchPlayer();
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Error al actualizar documento');
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'contract_representation': 'Contrato de Representación',
      'contract_club': 'Contrato con Club',
      'health_certificate': 'Carnet de Salud',
      'fitness_certificate': 'Carnet de Aptitud Física',
      'dni': 'DNI/Cédula',
      'parent_authorization': 'Autorización Padres',
      'medical_certificate': 'Certificado Médico',
      'school_id': 'Carnet Escolar',
      'other': 'Otro'
    };
    return labels[type] || type;
  };

  const getHealthCertificateFromDocs = () => {
    if (!player?.documents) return null;
    return player.documents.find(doc => doc.type === 'health_certificate');
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    // Parse date as local time to avoid timezone issues
    const [year, month, day] = birthDate.split('-').map(Number);
    const birth = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Sin categoría';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    // Parse date as local time by adding T12:00:00 to avoid timezone issues
    // This ensures the date is interpreted in the local timezone
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Health Certificate Status Helper
  const getHealthCertificateStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'missing', label: 'Sin carnet', color: 'gray', icon: XCircle };
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'expired', label: 'Vencido', color: 'red', icon: XCircle };
    } else if (diffDays <= 30) {
      return { status: 'expiring', label: 'Por vencer', color: 'yellow', icon: AlertCircle };
    } else {
      return { status: 'valid', label: 'Vigente', color: 'green', icon: CheckCircle };
    }
  };

  // Prepare data for progression chart
  const progressionChartData = useMemo(() => {
    if (!player?.tests || player.tests.length === 0) return [];
    
    // Group tests by month and calculate average per type
    const testsByMonth = {};
    const testTypes = ['física', 'técnica', 'táctica', 'psicológica'];
    
    player.tests.forEach(test => {
      // Parse date as YYYY-MM-DD string to avoid timezone issues
      const dateParts = test.date.split('-');
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]);
      const monthKey = `${year}-${String(month).padStart(2, '0')}`;
      
      if (!testsByMonth[monthKey]) {
        testsByMonth[monthKey] = {
          month: monthKey,
          física: [], técnica: [], táctica: [], psicológica: [], otra: []
        };
      }
      
      const type = testTypes.includes(test.test_type) ? test.test_type : 'otra';
      testsByMonth[monthKey][type].push(test.score);
    });
    
    // Calculate averages and format for chart
    const result = Object.keys(testsByMonth)
      .sort()
      .map(monthKey => {
        const data = testsByMonth[monthKey];
        const [year, month] = monthKey.split('-');
        const monthNames = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
        const monthLabel = `${monthNames[parseInt(month) - 1]} ${year.slice(-2)}`;
        
        const resultItem = { month: monthLabel };
        testTypes.forEach(type => {
          if (data[type].length > 0) {
            resultItem[type] = Math.round(data[type].reduce((a, b) => a + b, 0) / data[type].length);
          }
        });
        
        return resultItem;
      });
    
    console.log('Chart data months:', result.map(r => r.month));
    return result;
  }, [player?.tests?.length, player?.tests]);

  // Colors for chart lines
  const chartColors = {
    'física': '#f97316',
    'técnica': '#a855f7', 
    'táctica': '#3b82f6',
    'psicológica': '#ec4899'
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            <p className="mt-4 text-gray-600">Cargando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!player) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-600">Jugador no encontrado</p>
          <Button onClick={() => navigate('/players')} className="mt-4">
            Volver a Jugadores
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/players')}
            className="mb-2"
            data-testid="back-to-players"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Jugadores
          </Button>
          {editing ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setEditedPlayer(player);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                className="bg-brand-600 hover:bg-brand-700"
                data-testid="save-player-button"
              >
                <Save className="w-4 h-4 mr-2" />
                Guardar Cambios
              </Button>
            </div>
          ) : (
            // Para scouts: solo permite editar si es su jugador (lo creó o es el captador)
            (!isScout || (user?.id && (player.created_by === user.id || player.scouted_by === user.id))) && (
              <Button
                onClick={() => setEditing(true)}
                className="bg-brand-600 hover:bg-brand-700"
                data-testid="edit-player-button"
              >
                Editar Información
              </Button>
            )
          )}
        </div>

        {/* Header Card with Gradient Background */}
        <Card className="overflow-hidden border-0 shadow-xl">
          <div className="bg-gradient-to-r from-[#006A85] to-[#0891B2] h-24 md:h-32"></div>
          <CardContent className="relative -mt-12 md:-mt-16 pb-6">
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-center md:items-start">
              <div className="flex-shrink-0 relative group">
                {editing ? (
                  <div className="w-40 h-40">
                    <ImageUpload
                      value={editedPlayer.photo_url}
                      onChange={(url) => setEditedPlayer({...editedPlayer, photo_url: url})}
                    />
                  </div>
                ) : (
                  <div 
                    className={`relative ${player.photo_url ? 'cursor-pointer' : ''}`}
                    onClick={() => player.photo_url && setShowPhotoModal(true)}
                  >
                    <Avatar className="h-40 w-40 border-4 border-white shadow-2xl">
                      <AvatarImage src={player.photo_url} />
                      <AvatarFallback className="bg-gradient-to-br from-brand-100 to-brand-200 text-brand-700 text-4xl">
                        {player.first_name[0]}{player.last_name[0]}
                      </AvatarFallback>
                    </Avatar>
                    {player.photo_url && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-full transition-all flex items-center justify-center">
                        <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex-1 pt-2 md:pt-6 text-center md:text-left w-full">
                <div className="flex flex-col md:flex-row md:items-start justify-between mb-4 items-center md:items-start">
                  <div className="w-full md:w-auto">
                    <h1 className="text-2xl md:text-4xl font-bold text-gray-900 md:text-white mb-2 md:drop-shadow-lg break-words">
                      {player.first_name} {player.last_name}
                    </h1>
                    <div className="flex items-center gap-2 mb-3 justify-center md:justify-start text-gray-600">
                      <Shield className="w-5 h-5 opacity-80" />
                      <span className="text-lg md:text-xl font-medium">
                        {player.contract_club_name || player.origin_club || <span className="italic font-normal">Sin club</span>}
                      </span>
                      <Badge className={`ml-2 ${
                        player.status === 'active' ? 'bg-green-100 text-green-800' :
                        player.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {player.status === 'active' ? 'Activo' : player.status === 'trial' ? 'Prueba' : 'Inactivo'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-center md:justify-start">
                      <p className="text-gray-700 md:text-white text-base md:text-lg font-medium">{calculateAge(player.birth_date)} años</p>
                      <span className="text-gray-400 md:text-white/60">•</span>
                      <p className="text-gray-700 md:text-white text-base md:text-lg font-medium">
                        {(player.category_ids || [player.category_id]).filter(Boolean).map(catId => getCategoryName(catId)).join(' / ') || 'Sin categoría'}
                      </p>
                      {player.imported_from === 'excel' && (
                        <Badge className="bg-blue-100 text-blue-800 border border-blue-300">
                          📥 Importado
                        </Badge>
                      )}
                      {player.validation_status === 'pending' && (
                        <Badge className="bg-orange-100 text-orange-800 border border-orange-300">
                          ⏳ Pendiente de revisión
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0 flex flex-col items-start md:items-end gap-2">
                    <span className="text-sm text-gray-600 md:text-white font-semibold md:drop-shadow">Índice General</span>
                    <ScoreBadge score={player.overall_score} size="lg" max={100} />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {player.position && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">Posición</p>
                      <p className="font-semibold text-gray-900">{player.position}</p>
                    </div>
                  )}
                  {player.preferred_foot && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">Pierna Hábil</p>
                      <p className="font-semibold text-gray-900 capitalize">{player.preferred_foot}</p>
                    </div>
                  )}
                  {player.height && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">Altura</p>
                      <p className="font-semibold text-gray-900">{player.height} cm</p>
                    </div>
                  )}
                  {player.weight && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">Peso</p>
                      <p className="font-semibold text-gray-900">{player.weight} kg</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="grid w-full grid-cols-4 md:grid-cols-7 h-auto p-1 bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-200">
            <TabsTrigger 
              value="personal" 
              className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-brand-50 data-[state=active]:to-brand-100 data-[state=active]:text-brand-700 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-brand-600 py-2 md:py-3 font-semibold hover:bg-brand-50/50 transition-all text-xs md:text-sm"
            >
              <User className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Información</span>
              <span className="sm:hidden">Info</span>
            </TabsTrigger>
            <TabsTrigger 
              value="sports"
              className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-brand-50 data-[state=active]:to-brand-100 data-[state=active]:text-brand-700 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-brand-600 py-2 md:py-3 font-semibold hover:bg-brand-50/50 transition-all text-xs md:text-sm"
            >
              <Trophy className="w-4 h-4 mr-1 md:mr-2" />
              Deportivo
            </TabsTrigger>
            <TabsTrigger 
              value="investment"
              className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-green-50 data-[state=active]:to-green-100 data-[state=active]:text-green-700 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-green-600 py-2 md:py-3 font-semibold hover:bg-green-50/50 transition-all text-xs md:text-sm"
              data-testid="investment-tab"
            >
              <DollarSign className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Inversión</span>
              <span className="sm:hidden">$</span>
            </TabsTrigger>
            <TabsTrigger 
              value="tracking"
              className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-brand-50 data-[state=active]:to-brand-100 data-[state=active]:text-brand-700 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-brand-600 py-2 md:py-3 font-semibold hover:bg-brand-50/50 transition-all text-xs md:text-sm"
              data-testid="tracking-tab"
            >
              <ClipboardList className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Seguimiento</span>
              <span className="sm:hidden">Seg.</span>
            </TabsTrigger>
            <TabsTrigger 
              value="documents"
              className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-brand-50 data-[state=active]:to-brand-100 data-[state=active]:text-brand-700 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-brand-600 py-2 md:py-3 font-semibold hover:bg-brand-50/50 transition-all text-xs md:text-sm"
            >
              <FileText className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Documentos</span>
              <span className="sm:hidden">Docs</span>
            </TabsTrigger>
            <TabsTrigger 
              value="notes"
              className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-brand-50 data-[state=active]:to-brand-100 data-[state=active]:text-brand-700 data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-brand-600 py-2 md:py-3 font-semibold hover:bg-brand-50/50 transition-all text-xs md:text-sm"
            >
              <Plus className="w-4 h-4 mr-1 md:mr-2" />
              Notas
            </TabsTrigger>
          </TabsList>

          {/* Personal Info Tab */}
          <TabsContent value="personal">
            <Card>
              <CardContent className="pt-8">
                <div className="space-y-8">
                  {/* Datos Personales */}
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-brand-50 to-brand-100 border border-brand-200">
                        <User className="w-5 h-5 text-brand-700" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Datos Personales</h3>
                        <p className="text-xs text-gray-500">Información básica del jugador</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5 pl-1">
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Nombre Completo</Label>
                        {editing ? (
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              value={editedPlayer.first_name}
                              onChange={(e) => setEditedPlayer({...editedPlayer, first_name: e.target.value})}
                              placeholder="Nombre"
                              className="h-9"
                            />
                            <Input
                              value={editedPlayer.last_name}
                              onChange={(e) => setEditedPlayer({...editedPlayer, last_name: e.target.value})}
                              placeholder="Apellido"
                              className="h-9"
                            />
                          </div>
                        ) : (
                          <p className="text-base font-medium text-gray-900">{player.first_name} {player.last_name}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                          <Calendar className="w-3 h-3" />
                          Fecha de Nacimiento
                        </Label>
                        {editing ? (
                          <Input
                            type="date"
                            value={editedPlayer.birth_date}
                            onChange={(e) => setEditedPlayer({...editedPlayer, birth_date: e.target.value})}
                            className="h-9"
                          />
                        ) : (
                          <div>
                            <p className="text-base font-semibold text-gray-900">{formatDate(player.birth_date)}</p>
                            <p className="text-sm text-brand-600 font-medium">{calculateAge(player.birth_date)} años</p>
                          </div>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                          <FileText className="w-3 h-3" />
                          Documento de Identidad
                        </Label>
                        {editing ? (
                          <Input
                            value={editedPlayer.document}
                            onChange={(e) => setEditedPlayer({...editedPlayer, document: e.target.value})}
                            className="h-9"
                          />
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <p className="text-base font-semibold text-gray-900">{player.document || <span className="text-gray-400 italic">Sin dato</span>}</p>
                              {player.documents?.find(doc => doc.type === 'dni') ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Documento disponible
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500 text-xs">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Sin documento
                                </Badge>
                              )}
                            </div>
                            {player.documents?.find(doc => doc.type === 'dni') && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs text-brand-600 hover:text-brand-700 hover:bg-brand-50 border-brand-200"
                                onClick={() => {
                                  const tabs = document.querySelector('[role="tablist"]');
                                  const docTab = Array.from(tabs.querySelectorAll('[role="tab"]')).find(t => t.textContent.includes('Documentos'));
                                  docTab?.click();
                                }}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Ver documento en repositorio
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                          <Calendar className="w-3 h-3" />
                          Fecha de Ingreso al Club
                        </Label>
                        {editing ? (
                          <Input
                            type="date"
                            value={editedPlayer.join_date || ''}
                            onChange={(e) => setEditedPlayer({...editedPlayer, join_date: e.target.value})}
                            className="h-9"
                          />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">
                            {player.join_date ? formatDate(player.join_date) : <span className="text-gray-400 font-normal">No especificado</span>}
                          </p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                          <Phone className="w-3 h-3" />
                          Teléfono
                        </Label>
                        {editing ? (
                          <Input
                            value={editedPlayer.phone || ''}
                            onChange={(e) => setEditedPlayer({...editedPlayer, phone: e.target.value})}
                            className="h-9"
                          />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.phone || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          Email
                        </Label>
                        {editing ? (
                          <Input
                            type="email"
                            value={editedPlayer.email || ''}
                            onChange={(e) => setEditedPlayer({...editedPlayer, email: e.target.value})}
                            className="h-9"
                          />
                        ) : (
                          <p className="text-base font-semibold text-gray-900 break-all">{player.email || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                          <UserPlus className="w-3 h-3" />
                          Teléfono Familiar/Emergencia
                        </Label>
                        {editing ? (
                          <Input
                            value={editedPlayer.emergency_phone || ''}
                            onChange={(e) => setEditedPlayer({...editedPlayer, emergency_phone: e.target.value})}
                            className="h-9"
                            placeholder="Ej: 099123456"
                          />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.emergency_phone || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                          <User className="w-3 h-3" />
                          Contacto de Emergencia
                        </Label>
                        {editing ? (
                          <Input
                            value={editedPlayer.emergency_contact_name || ''}
                            onChange={(e) => setEditedPlayer({...editedPlayer, emergency_contact_name: e.target.value})}
                            className="h-9"
                            placeholder="Ej: Madre, Padre, Tutor"
                          />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.emergency_contact_name || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                          <Shield className="w-3 h-3" />
                          Mutualista / Cobertura Médica
                        </Label>
                        {editing ? (
                          <SelectWithAdd
                            catalogType="medical_coverages"
                            value={editedPlayer.medical_coverage || ''}
                            onChange={(value) => setEditedPlayer({...editedPlayer, medical_coverage: value})}
                            placeholder="Selecciona mutualista..."
                            testId="edit-medical-coverage"
                            addLabel="Agregar mutualista"
                          />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.medical_coverage || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          Dirección
                        </Label>
                        {editing ? (
                          <div className="space-y-2">
                            <Input
                              value={editedPlayer.address || ''}
                              onChange={(e) => setEditedPlayer({...editedPlayer, address: e.target.value})}
                              className="h-9"
                              placeholder="Ej: Av. Italia 2450, Montevideo"
                            />
                            <p className="text-xs text-gray-500">
                              💡 La dirección se geocodificará automáticamente para el mapa
                            </p>
                          </div>
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.address || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>
                      
                      {/* Coordinates display (read-only in edit, shown in view) */}
                      {(editing && (editedPlayer.latitude || editedPlayer.longitude)) || (!editing && (player.latitude || player.longitude)) ? (
                        <div className="col-span-2">
                          <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                            Coordenadas (Geolocalizacion)
                          </Label>
                          {editing ? (
                            <div className="flex gap-4 items-center">
                              <div className="flex-1">
                                <Label className="text-xs">Latitud</Label>
                                <Input
                                  type="number"
                                  step="0.0001"
                                  value={editedPlayer.latitude || ''}
                                  onChange={(e) => setEditedPlayer({...editedPlayer, latitude: parseFloat(e.target.value) || null})}
                                  className="h-9"
                                  placeholder="-34.9011"
                                />
                              </div>
                              <div className="flex-1">
                                <Label className="text-xs">Longitud</Label>
                                <Input
                                  type="number"
                                  step="0.0001"
                                  value={editedPlayer.longitude || ''}
                                  onChange={(e) => setEditedPlayer({...editedPlayer, longitude: parseFloat(e.target.value) || null})}
                                  className="h-9"
                                  placeholder="-56.1645"
                                />
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-700">
                              Lat: {player.latitude?.toFixed(4)}, Lng: {player.longitude?.toFixed(4)}
                            </p>
                          )}
                        </div>
                      ) : null}

                      {/* Agency fields */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Nacionalidad</Label>
                        {editing ? (
                          <SelectWithAdd
                            catalogType="nationalities"
                            value={editedPlayer.nationality || ''}
                            onChange={(value) => setEditedPlayer({...editedPlayer, nationality: value})}
                            placeholder="Selecciona nacionalidad..."
                            testId="edit-nationality"
                            addLabel="Agregar nacionalidad"
                          />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.nationality || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Pasaporte</Label>
                        {editing ? (
                          <Input value={editedPlayer.passport || ''} onChange={(e) => setEditedPlayer({...editedPlayer, passport: e.target.value})} className="h-9" />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.passport || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Pasaporte Comunitario</Label>
                        {editing ? (
                          <Select
                            value={editedPlayer.community_passport || ''}
                            onValueChange={(value) => setEditedPlayer({...editedPlayer, community_passport: value})}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Selecciona" />
                            </SelectTrigger>
                            <SelectContent>
                              {COMMUNITY_PASSPORT_OPTIONS.map(o => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.community_passport || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Redes Sociales</Label>
                        {editing ? (
                          <Input value={editedPlayer.social_media || ''} onChange={(e) => setEditedPlayer({...editedPlayer, social_media: e.target.value})} className="h-9" placeholder="@instagram, @tiktok, etc." />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.social_media || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Talle de Botines (US)</Label>
                        {editing ? (
                          <SelectWithAdd
                            catalogType="shoe_sizes"
                            value={editedPlayer.shoe_size || ''}
                            onChange={(value) => setEditedPlayer({...editedPlayer, shoe_size: value})}
                            placeholder="Selecciona talle..."
                            testId="edit-shoe-size"
                            addLabel="Agregar talle"
                          />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.shoe_size || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1 md:col-span-3">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Trayectoria de Clubes anteriores</Label>
                        {editing ? (
                          <Input value={editedPlayer.club_trajectory || ''} onChange={(e) => setEditedPlayer({...editedPlayer, club_trajectory: e.target.value})} className="h-9" placeholder="Ej: Nacional (2020-2022), Fénix (2022-actual)" />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.club_trajectory || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Familia / Tutor */}
                  <div className="border-t pt-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
                        <User className="w-5 h-5 text-purple-700" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Familia / Tutor</h3>
                        <p className="text-xs text-gray-500">Datos de contacto familiar</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5 pl-1">
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Nombre Madre</Label>
                        {editing ? (
                          <Input value={editedPlayer.mother_name || ''} onChange={(e) => setEditedPlayer({...editedPlayer, mother_name: e.target.value})} className="h-9" />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.mother_name || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Celular Madre</Label>
                        {editing ? (
                          <Input value={editedPlayer.mother_phone || ''} onChange={(e) => setEditedPlayer({...editedPlayer, mother_phone: e.target.value})} className="h-9" />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.mother_phone || <span className="text-gray-400 font-normal">-</span>}</p>
                        )}
                      </div>
                      <div></div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Nombre Padre</Label>
                        {editing ? (
                          <Input value={editedPlayer.father_name || ''} onChange={(e) => setEditedPlayer({...editedPlayer, father_name: e.target.value})} className="h-9" />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.father_name || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Celular Padre</Label>
                        {editing ? (
                          <Input value={editedPlayer.father_phone || ''} onChange={(e) => setEditedPlayer({...editedPlayer, father_phone: e.target.value})} className="h-9" />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.father_phone || <span className="text-gray-400 font-normal">-</span>}</p>
                        )}
                      </div>
                      <div></div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Nombre Tutor</Label>
                        {editing ? (
                          <Input value={editedPlayer.tutor_name || ''} onChange={(e) => setEditedPlayer({...editedPlayer, tutor_name: e.target.value})} className="h-9" />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.tutor_name || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Celular Tutor</Label>
                        {editing ? (
                          <Input value={editedPlayer.tutor_phone || ''} onChange={(e) => setEditedPlayer({...editedPlayer, tutor_phone: e.target.value})} className="h-9" />
                        ) : (
                          <p className="text-base font-semibold text-gray-900">{player.tutor_phone || <span className="text-gray-400 font-normal">-</span>}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
                        <GraduationCap className="w-5 h-5 text-blue-700" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Información Académica</h3>
                        <p className="text-xs text-gray-500">Rendimiento y educación</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 pl-1">
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Institución Educativa</Label>
                        {editing ? (
                          <Input
                            value={editedPlayer.educational_institution || ''}
                            onChange={(e) => setEditedPlayer({...editedPlayer, educational_institution: e.target.value})}
                            className="h-9"
                          />
                        ) : (
                          <p className="text-base font-medium text-gray-900">{player.educational_institution || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Rendimiento Académico</Label>
                        {editing ? (
                          <Select
                            value={editedPlayer.academic_performance || ''}
                            onValueChange={(value) => setEditedPlayer({...editedPlayer, academic_performance: value})}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Selecciona" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="excelente">Excelente</SelectItem>
                              <SelectItem value="bueno">Bueno</SelectItem>
                              <SelectItem value="regular">Regular</SelectItem>
                              <SelectItem value="necesita_apoyo">Necesita Apoyo</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-base font-medium text-gray-900 capitalize">{player.academic_performance?.replace('_', ' ') || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sports Info Tab */}
          <TabsContent value="sports">
            <Card>
              <CardContent className="pt-8">
                <div className="space-y-8">
                  {/* Datos Deportivos */}
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200">
                        <Trophy className="w-5 h-5 text-emerald-700" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Datos Deportivos</h3>
                        <p className="text-xs text-gray-500">Categoría, posición y estado</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5 pl-1">
                      <div className="space-y-1 md:col-span-2">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Categorías (máx. 2)</Label>
                        {editing ? (
                          <div className="flex flex-wrap gap-2">
                            {categories.map(cat => {
                              const isSelected = (editedPlayer.category_ids || [editedPlayer.category_id]).includes(cat.id);
                              const currentIds = editedPlayer.category_ids || (editedPlayer.category_id ? [editedPlayer.category_id] : []);
                              const canSelect = isSelected || currentIds.length < 2;
                              
                              return (
                                <button
                                  key={cat.id}
                                  type="button"
                                  disabled={!canSelect && !isSelected}
                                  onClick={() => {
                                    let newIds;
                                    if (isSelected) {
                                      // Remove
                                      newIds = currentIds.filter(id => id !== cat.id);
                                    } else if (currentIds.length < 2) {
                                      // Add
                                      newIds = [...currentIds, cat.id];
                                    } else {
                                      return;
                                    }
                                    setEditedPlayer({
                                      ...editedPlayer, 
                                      category_ids: newIds,
                                      category_id: newIds[0] || null
                                    });
                                  }}
                                  className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                                    isSelected 
                                      ? 'bg-brand-600 text-white border-brand-600' 
                                      : canSelect
                                        ? 'bg-white text-gray-700 border-gray-300 hover:border-brand-400'
                                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                  }`}
                                >
                                  {isSelected && <span className="mr-1">✓</span>}
                                  {cat.name}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {(player.category_ids || [player.category_id]).filter(Boolean).map((catId, idx) => (
                              <span key={catId} className={`px-3 py-1 text-sm rounded-full ${
                                idx === 0 ? 'bg-brand-100 text-brand-800' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {getCategoryName(catId)}
                                {idx === 0 && (player.category_ids?.length > 1 || false) && <span className="ml-1 text-xs">(principal)</span>}
                              </span>
                            ))}
                            {!(player.category_ids?.length || player.category_id) && (
                              <span className="text-gray-400 italic">Sin categoría</span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Estado</Label>
                        {editing ? (
                          <Select
                            value={editedPlayer.status}
                            onValueChange={(value) => setEditedPlayer({...editedPlayer, status: value})}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Activo</SelectItem>
                              <SelectItem value="inactive">Inactivo</SelectItem>
                              <SelectItem value="trial">Prueba</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-base font-medium text-gray-900 capitalize">{player.status === 'active' ? 'Activo' : player.status === 'trial' ? 'Prueba' : 'Inactivo'}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Pierna Hábil</Label>
                        {editing ? (
                          <Select
                            value={editedPlayer.preferred_foot || ''}
                            onValueChange={(value) => setEditedPlayer({...editedPlayer, preferred_foot: value})}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Selecciona" />
                            </SelectTrigger>
                            <SelectContent>
                              {FOOT_OPTIONS.map(f => (
                                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-base font-medium text-gray-900">{player.preferred_foot || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Posición Principal</Label>
                        {editing ? (
                          <SelectWithAdd
                            catalogType="positions"
                            value={editedPlayer.position || ''}
                            onChange={(value) => setEditedPlayer({...editedPlayer, position: value})}
                            placeholder="Selecciona posición..."
                            testId="edit-position"
                            addLabel="Agregar posición"
                          />
                        ) : (
                          <p className="text-base font-medium text-gray-900">{player.position || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase">Posición Secundaria / Aclaración</Label>
                        {editing ? (
                          <Input
                            value={editedPlayer.position_secondary || ''}
                            onChange={(e) => setEditedPlayer({...editedPlayer, position_secondary: e.target.value})}
                            className="h-9"
                            placeholder="Ej: también juega de extremo"
                          />
                        ) : (
                          <p className="text-base font-medium text-gray-900">{player.position_secondary || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Certificados Médicos */}
                  <div className="border-t pt-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-red-50 to-red-100 border border-red-200">
                        <FileText className="w-5 h-5 text-red-700" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Certificados Médicos</h3>
                        <p className="text-xs text-gray-500">Documentación de salud y aptitud física</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 pl-1">
                      {/* Carnet de Salud */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                          <Heart className="w-3 h-3" />
                          Carnet de Salud - Vencimiento
                        </Label>
                        {editing ? (
                          <Input
                            type="date"
                            value={editedPlayer.health_certificate_expiry || ''}
                            onChange={(e) => setEditedPlayer({...editedPlayer, health_certificate_expiry: e.target.value})}
                            className="h-9"
                          />
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              {player.health_certificate_expiry ? (
                                <>
                                  <p className="text-base font-semibold text-gray-900">{formatDate(player.health_certificate_expiry)}</p>
                                  {(() => {
                                    const certStatus = getHealthCertificateStatus(player.health_certificate_expiry);
                                    return (
                                      <Badge className={
                                        certStatus.color === 'green' ? 'bg-green-100 text-green-800' :
                                        certStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                        certStatus.color === 'red' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                      }>
                                        {certStatus.label}
                                      </Badge>
                                    );
                                  })()}
                                </>
                              ) : (
                                <p className="text-base text-gray-400 font-normal">No especificado</p>
                              )}
                              {player.documents?.find(doc => doc.type === 'health_certificate') ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Documento disponible
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500 text-xs">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Sin documento
                                </Badge>
                              )}
                            </div>
                            {player.documents?.find(doc => doc.type === 'health_certificate') && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs text-brand-600 hover:text-brand-700 hover:bg-brand-50 border-brand-200"
                                onClick={() => {
                                  const tabs = document.querySelector('[role="tablist"]');
                                  const docTab = Array.from(tabs.querySelectorAll('[role="tab"]')).find(t => t.textContent.includes('Documentos'));
                                  docTab?.click();
                                }}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Ver documento en repositorio
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Carnet de Aptitud Física */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                          <Activity className="w-3 h-3" />
                          Carnet de Aptitud Física - Vencimiento
                        </Label>
                        {editing ? (
                          <Input
                            type="date"
                            value={editedPlayer.fitness_certificate_expiry || ''}
                            onChange={(e) => setEditedPlayer({...editedPlayer, fitness_certificate_expiry: e.target.value})}
                            className="h-9"
                          />
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              {player.fitness_certificate_expiry ? (
                                <>
                                  <p className="text-base font-semibold text-gray-900">{formatDate(player.fitness_certificate_expiry)}</p>
                                  {(() => {
                                    const certStatus = getHealthCertificateStatus(player.fitness_certificate_expiry);
                                    return (
                                      <Badge className={
                                        certStatus.color === 'green' ? 'bg-green-100 text-green-800' :
                                        certStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                        certStatus.color === 'red' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                      }>
                                        {certStatus.label}
                                      </Badge>
                                    );
                                  })()}
                                </>
                              ) : (
                                <p className="text-base text-gray-400 font-normal">No especificado</p>
                              )}
                              {player.documents?.find(doc => doc.type === 'fitness_certificate') ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Documento disponible
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500 text-xs">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Sin documento
                                </Badge>
                              )}
                            </div>
                            {player.documents?.find(doc => doc.type === 'fitness_certificate') && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs text-brand-600 hover:text-brand-700 hover:bg-brand-50 border-brand-200"
                                onClick={() => {
                                  const tabs = document.querySelector('[role="tablist"]');
                                  const docTab = Array.from(tabs.querySelectorAll('[role="tab"]')).find(t => t.textContent.includes('Documentos'));
                                  docTab?.click();
                                }}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Ver documento en repositorio
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Datos Físicos y Club de Origen - En la misma fila */}
                  <div className="border-t pt-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Datos Físicos */}
                      <div>
                        <div className="flex items-center gap-3 mb-6">
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200">
                            <Ruler className="w-5 h-5 text-orange-700" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Datos Físicos</h3>
                            <p className="text-xs text-gray-500">Medidas y características</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-y-5 pl-1">
                          <div className="space-y-1">
                            <Label className="text-xs font-medium text-gray-500 uppercase">Altura</Label>
                            {editing ? (
                              <Input
                                type="number"
                                step="0.1"
                                value={editedPlayer.height || ''}
                                onChange={(e) => setEditedPlayer({...editedPlayer, height: e.target.value})}
                                className="h-9"
                              />
                            ) : (
                              <p className="text-base font-medium text-gray-900">{player.height ? `${player.height} cm` : <span className="text-gray-400 font-normal">No especificado</span>}</p>
                            )}
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs font-medium text-gray-500 uppercase">Peso</Label>
                            {editing ? (
                              <Input
                                type="number"
                                step="0.1"
                                value={editedPlayer.weight || ''}
                                onChange={(e) => setEditedPlayer({...editedPlayer, weight: e.target.value})}
                                className="h-9"
                              />
                            ) : (
                              <p className="text-base font-medium text-gray-900">{player.weight ? `${player.weight} kg` : <span className="text-gray-400 font-normal">No especificado</span>}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Club de Origen */}
                      <div>
                        <div className="flex items-center gap-3 mb-6">
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
                            <Home className="w-5 h-5 text-purple-700" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Club de Origen</h3>
                            <p className="text-xs text-gray-500">Procedencia y experiencia</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-y-5 pl-1">
                          <div className="space-y-1">
                            <Label className="text-xs font-medium text-gray-500 uppercase">Equipo actual</Label>
                            {editing ? (
                              <SelectWithAdd
                                catalogType="teams"
                                value={editedPlayer.origin_club || ''}
                                onChange={(value) => setEditedPlayer({...editedPlayer, origin_club: value})}
                                placeholder="Selecciona equipo..."
                                testId="edit-team"
                                addLabel="Agregar equipo"
                              />
                            ) : (
                              <p className="text-base font-medium text-gray-900">{player.origin_club || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                            )}
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs font-medium text-gray-500 uppercase">Años de Experiencia</Label>
                            {editing ? (
                              <Input
                                type="number"
                                value={editedPlayer.years_experience || ''}
                                onChange={(e) => setEditedPlayer({...editedPlayer, years_experience: e.target.value})}
                                className="h-9"
                              />
                            ) : (
                              <p className="text-base font-medium text-gray-900">{player.years_experience ? `${player.years_experience} años` : <span className="text-gray-400 font-normal">No especificado</span>}</p>
                            )}
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs font-medium text-gray-500 uppercase">Traído por (Planilla)</Label>
                            <p className="text-base font-medium text-gray-900">{player.brought_by || <span className="text-gray-400 font-normal">No especificado</span>}</p>
                            <p className="text-xs text-gray-400">Dato de importación Excel</p>
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs font-medium text-gray-500 uppercase">Captador Asignado</Label>
                            {editing ? (
                              <Select 
                                value={editedPlayer.scouted_by || '__none__'} 
                                onValueChange={(v) => setEditedPlayer({...editedPlayer, scouted_by: v === '__none__' ? null : v})}
                              >
                                <SelectTrigger className="h-9" data-testid="scouted-by-select">
                                  <SelectValue placeholder="Sin asignar" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">Sin asignar</SelectItem>
                                  {scouts.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                      {s.full_name || s.username}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="text-base font-medium text-gray-900">
                                {(() => {
                                  if (!player.scouted_by) return <span className="text-gray-400 font-normal">Sin asignar</span>;
                                  const s = scouts.find((u) => u.id === player.scouted_by);
                                  return s ? (s.full_name || s.username) : player.scouted_by;
                                })()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tracking Tab — Seguimientos del jugador (visitas a partidos/prácticas) */}
          <TabsContent value="tracking">
            <div className="space-y-4">
              {/* Header con filtro */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Comentarios de scouting</h3>
                  <p className="text-sm text-gray-500">
                    Observaciones registradas en partidos y prácticas
                  </p>
                </div>
                <Select value={trackingFilter} onValueChange={setTrackingFilter}>
                  <SelectTrigger className="w-full sm:w-48 h-9 text-sm" data-testid="tracking-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los eventos</SelectItem>
                    <SelectItem value="partido">Solo partidos</SelectItem>
                    <SelectItem value="entrenamiento">Solo prácticas</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Resumen stats */}
              {trackingData.length > 0 && (() => {
                const ratingsWithDates = trackingData
                  .map(t => {
                    const obs = (t.players_observed || []).find(p => p.player_id === id);
                    return { rating: obs?.rating, date: t.date, type: t.event_type };
                  })
                  .filter(r => r.rating != null && r.rating > 0)
                  .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
                const lastRating = ratingsWithDates[0];

                // Métricas adicionales
                const now = Date.now();
                const last30 = trackingData.filter(t => {
                  if (!t.date) return false;
                  const d = new Date(t.date + 'T12:00:00').getTime();
                  return (now - d) <= 30 * 24 * 60 * 60 * 1000;
                }).length;
                const uniqueScouts = new Set(trackingData.map(t => t.scout_id).filter(Boolean)).size;
                const lastDate = trackingData[0]?.date;
                const daysSinceLast = lastDate ? Math.floor((now - new Date(lastDate + 'T12:00:00').getTime()) / (1000 * 60 * 60 * 24)) : null;
                const isStale = daysSinceLast != null && daysSinceLast >= 60;

                return (
                  <>
                    {isStale && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2" data-testid="stale-warning">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <p className="text-sm text-amber-800">
                          Este jugador no recibe visitas hace <strong>{daysSinceLast} días</strong>. Coordiná una nueva observación.
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <Card className="bg-gradient-to-br from-brand-50 to-brand-100 border-brand-200">
                        <CardContent className="pt-4 pb-4 text-center">
                          <p className="text-xs text-brand-700 font-medium mb-1">Visitas registradas</p>
                          <p className="text-2xl font-bold text-brand-800">{trackingData.length}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                        <CardContent className="pt-4 pb-4 text-center">
                          <p className="text-xs text-blue-700 font-medium mb-1">Últimos 30 días</p>
                          <p className="text-2xl font-bold text-blue-800">{last30}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                        <CardContent className="pt-4 pb-4 text-center">
                          <p className="text-xs text-purple-700 font-medium mb-1">Scouts distintos</p>
                          <p className="text-2xl font-bold text-purple-800">{uniqueScouts}</p>
                        </CardContent>
                      </Card>
                      {lastRating ? (
                        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                          <CardContent className="pt-4 pb-4 text-center">
                            <p className="text-xs text-amber-700 font-medium mb-1">Última calificación</p>
                            <p className="text-2xl font-bold text-amber-800">⭐ {lastRating.rating}/10</p>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card className={`${isStale ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-200' : 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200'}`}>
                          <CardContent className="pt-4 pb-4 text-center">
                            <p className={`text-xs ${isStale ? 'text-red-700' : 'text-emerald-700'} font-medium mb-1`}>Última observación</p>
                            <p className={`text-sm font-semibold ${isStale ? 'text-red-800' : 'text-emerald-800'}`}>
                              {lastDate ? `Hace ${daysSinceLast}d` : '—'}
                            </p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Lista de comentarios */}
              {loadingTracking ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
                </div>
              ) : trackingData.length === 0 ? (
                <Card className="p-10 text-center">
                  <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium">Sin comentarios de scouting aún</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Cuando un scout registre una visita observando a este jugador, aparecerá acá
                  </p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {trackingData.map((rec) => {
                    const obs = (rec.players_observed || []).find(p => p.player_id === id) || {};
                    const isPartido = rec.event_type === 'partido';
                    const EventIcon = isPartido ? Trophy : Dumbbell;
                    return (
                      <Card
                        key={rec.id}
                        className="hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/seguimiento/${rec.id}`)}
                        data-testid={`tracking-entry-${rec.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`shrink-0 p-2 rounded-lg ${isPartido ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              <EventIcon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <Badge variant="outline" className="text-xs">
                                  {isPartido ? 'Partido' : 'Práctica'}
                                </Badge>
                                <span className="text-xs text-gray-500">
                                  {rec.date ? new Date(rec.date).toLocaleDateString('es-UY', { day: '2-digit', month: 'long', year: 'numeric' }) : '-'}
                                </span>
                                {obs.rating != null && (
                                  <Badge className="bg-brand-100 text-brand-700 text-xs">
                                    ⭐ {obs.rating}/10
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm font-semibold text-gray-900">
                                {rec.club || 'Sin equipo'}
                                {rec.opponent && <span className="text-gray-500 font-normal"> vs {rec.opponent}</span>}
                              </p>
                              {rec.location && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  <MapPin className="inline w-3 h-3 mr-0.5" />
                                  {rec.location}
                                </p>
                              )}
                              {obs.comments ? (
                                <div className="mt-3 p-3 bg-gray-50 rounded-md">
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{obs.comments}</p>
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400 italic mt-2">
                                  Sin comentarios para este jugador en esta visita
                                </p>
                              )}
                              {rec.scout_name && (
                                <p className="text-xs text-gray-400 mt-2">
                                  Por {rec.scout_name}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>



          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader className="border-b bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Repositorio de Documentos</CardTitle>
                    <CardDescription>Almacenamiento centralizado de todos los documentos del jugador</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex items-center border rounded-lg">
                      <Button
                        variant={documentView === 'list' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setDocumentView('list')}
                        className="rounded-r-none"
                      >
                        <List className="w-4 h-4" />
                      </Button>
                      <Button
                        variant={documentView === 'preview' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setDocumentView('preview')}
                        className="rounded-l-none"
                      >
                        <Grid className="w-4 h-4" />
                      </Button>
                    </div>
                    <Button
                      onClick={() => {
                        // Pre-llenar representante con el scout asignado al jugador (si lo hay)
                        const assignedScout = player?.scouted_by
                          ? scouts.find((s) => s.id === player.scouted_by)
                          : null;
                        const prefillName = assignedScout
                          ? (assignedScout.full_name || assignedScout.username)
                          : '';
                        setNewDocument((prev) => ({
                          ...prev,
                          metadata: { ...prev.metadata, representative_name: prefillName },
                        }));
                        setShowAddDocument(true);
                      }}
                      size="sm"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Documento
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {!player.documents || player.documents.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium text-gray-600">No hay documentos cargados</p>
                    <p className="text-sm mt-2">Haz click en &quot;Agregar Documento&quot; para comenzar</p>
                  </div>
                ) : (
                  <>
                    {/* LIST VIEW */}
                    {documentView === 'list' && (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vencimiento</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {player.documents.map((doc) => {
                              // Determine expiry date based on document type
                              const expiryDate = doc.type === 'contract_club' || doc.type === 'contract_representation' 
                                ? doc.metadata?.end_date 
                                : doc.metadata?.expiry_date;
                              const docStatus = expiryDate ? getHealthCertificateStatus(expiryDate) : null;
                              
                              // Contract specific info
                              const isContract = doc.type === 'contract_club' || doc.type === 'contract_representation';
                              const contractInfo = isContract ? (
                                doc.type === 'contract_club' 
                                  ? doc.metadata?.club_name 
                                  : doc.metadata?.representative_name
                              ) : null;
                              
                              return (
                                <tr key={doc.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3">
                                    <Badge variant="outline" className={isContract ? 'bg-blue-50 border-blue-200' : ''}>
                                      {getDocumentTypeLabel(doc.type)}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="font-medium text-gray-900">{doc.name}</div>
                                    {contractInfo && (
                                      <div className="text-xs text-blue-600 mt-0.5">
                                        {doc.type === 'contract_club' ? '🏟️ ' : '👤 '}{contractInfo}
                                      </div>
                                    )}
                                    {isContract && doc.metadata?.start_date && (
                                      <div className="text-xs text-gray-500 mt-0.5">
                                        Desde: {formatDate(doc.metadata.start_date)}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-600">
                                    {formatDate(doc.upload_date)}
                                  </td>
                                  <td className="px-4 py-3">
                                    {docStatus && (
                                      <Badge className={
                                        docStatus.color === 'green' ? 'bg-green-100 text-green-800' :
                                        docStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                      }>
                                        {isContract ? 'Vence: ' : ''}{formatDate(expiryDate)}
                                      </Badge>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex justify-end gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => window.open(doc.file, '_blank')}
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditDocument(doc)}
                                        title="Editar documento"
                                      >
                                        <Edit className="w-4 h-4 text-blue-600" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteDocument(doc.id)}
                                      >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* PREVIEW VIEW */}
                    {documentView === 'preview' && (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {player.documents.map((doc) => {
                          const docStatus = doc.metadata?.expiry_date ? getHealthCertificateStatus(doc.metadata.expiry_date) : null;
                          return (
                            <div key={doc.id} className="border rounded-lg p-3 hover:shadow-md transition-shadow">
                              <div className="aspect-square bg-gray-100 rounded-lg mb-3 overflow-hidden cursor-pointer" onClick={() => window.open(doc.file, '_blank')}>
                                <img src={doc.file} alt={doc.name} className="w-full h-full object-cover" />
                              </div>
                              <div className="space-y-2">
                                <Badge variant="outline" className="text-xs">{getDocumentTypeLabel(doc.type)}</Badge>
                                <p className="font-medium text-sm text-gray-900 truncate">{doc.name}</p>
                                <p className="text-xs text-gray-500">{formatDate(doc.upload_date)}</p>
                                {docStatus && (
                                  <Badge className={`text-xs ${
                                    docStatus.color === 'green' ? 'bg-green-100 text-green-800' :
                                    docStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {docStatus.label}
                                  </Badge>
                                )}
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => handleEditDocument(doc)}
                                  >
                                    <Edit className="w-4 h-4 mr-1" />
                                    Editar
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteDocument(doc.id)}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {/* Edit Document Dialog */}
                {editingDocument && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                      <CardHeader>
                        <CardTitle className="text-lg">Editar Documento</CardTitle>
                        <CardDescription>Actualizá la información del documento</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Tipo de Documento</Label>
                          <Select
                            value={editingDocument.type || 'other'}
                            onValueChange={(v) => setEditingDocument({ ...editingDocument, type: v })}
                          >
                            <SelectTrigger data-testid="edit-doc-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="contract_representation">Contrato de Representación</SelectItem>
                              <SelectItem value="contract_club">Contrato con Club</SelectItem>
                              <SelectItem value="health_certificate">Carnet de Salud</SelectItem>
                              <SelectItem value="fitness_certificate">Carnet de Aptitud Física</SelectItem>
                              <SelectItem value="dni">DNI / Cédula</SelectItem>
                              <SelectItem value="parent_authorization">Autorización Padres</SelectItem>
                              <SelectItem value="medical_certificate">Certificado Médico</SelectItem>
                              <SelectItem value="school_id">Carnet Escolar</SelectItem>
                              <SelectItem value="other">Otro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Nombre</Label>
                          <Input
                            value={editingDocument.name}
                            onChange={(e) => setEditingDocument({...editingDocument, name: e.target.value})}
                          />
                        </div>

                        {/* Current file preview */}
                        <div className="space-y-2">
                          <Label>Archivo Actual</Label>
                          <div className="border rounded-lg p-3 bg-gray-50">
                            {editingDocument.file ? (
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded bg-white border flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-6 h-6 text-brand-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-700 truncate font-medium">{editingDocument.file_name || editingDocument.file.split('/').pop()}</p>
                                  <a
                                    href={editingDocument.file.startsWith('http') ? editingDocument.file : `${API.replace('/api', '')}${editingDocument.file}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-brand-600 hover:underline"
                                  >
                                    Abrir / Descargar
                                  </a>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">Sin archivo</p>
                            )}
                          </div>
                        </div>

                        {/* Upload new file */}
                        <div className="space-y-2">
                          <Label>Reemplazar Archivo (opcional)</Label>
                          <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-brand-400 transition-colors">
                            <input
                              type="file"
                              id="edit-doc-file"
                              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.odt,.ods,.csv,.txt"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setEditingDocument({
                                      ...editingDocument,
                                      newFile: reader.result,
                                      newFileName: file.name
                                    });
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                            <label htmlFor="edit-doc-file" className="cursor-pointer">
                              {editingDocument.newFile ? (
                                <div className="space-y-1">
                                  <FileText className="w-8 h-8 mx-auto text-emerald-600" />
                                  <p className="text-sm text-emerald-700 font-medium">
                                    ✓ {editingDocument.newFileName}
                                  </p>
                                  <p className="text-xs text-gray-500">Click para cambiar</p>
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <Camera className="w-7 h-7 mx-auto text-gray-400" />
                                  <p className="text-sm text-gray-600">Click para subir un nuevo archivo</p>
                                  <p className="text-xs text-gray-400">PDF, Word, Excel, imágenes — hasta 15MB</p>
                                </div>
                              )}
                            </label>
                          </div>
                        </div>

                        {/* Campos específicos por tipo de documento */}
                        {(editingDocument.type === 'contract_club' || editingDocument.type === 'contract_representation') && (
                          <>
                            {editingDocument.type === 'contract_club' && (
                              <div className="space-y-2">
                                <Label>Club</Label>
                                <Input
                                  value={editingDocument.metadata?.club_name || ''}
                                  onChange={(e) => setEditingDocument({
                                    ...editingDocument,
                                    metadata: { ...editingDocument.metadata, club_name: e.target.value }
                                  })}
                                  placeholder="Nombre del club"
                                />
                              </div>
                            )}
                            {editingDocument.type === 'contract_representation' && (
                              <div className="space-y-2">
                                <Label>Representante</Label>
                                <Input
                                  value={editingDocument.metadata?.representative_name || ''}
                                  onChange={(e) => setEditingDocument({
                                    ...editingDocument,
                                    metadata: { ...editingDocument.metadata, representative_name: e.target.value }
                                  })}
                                  placeholder="Nombre del representante / agencia"
                                  disabled={isLegales}
                                />
                                {isLegales && (
                                  <p className="text-xs text-gray-500">
                                    El representante solo puede ser modificado por un admin.
                                  </p>
                                )}
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-2">
                                <Label>Inicio</Label>
                                <Input
                                  type="date"
                                  value={editingDocument.metadata?.start_date || ''}
                                  onChange={(e) => setEditingDocument({
                                    ...editingDocument,
                                    metadata: { ...editingDocument.metadata, start_date: e.target.value }
                                  })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Fin</Label>
                                <Input
                                  type="date"
                                  value={editingDocument.metadata?.end_date || ''}
                                  onChange={(e) => setEditingDocument({
                                    ...editingDocument,
                                    metadata: { ...editingDocument.metadata, end_date: e.target.value }
                                  })}
                                />
                              </div>
                            </div>
                          </>
                        )}

                        {(editingDocument.type === 'health_certificate' || editingDocument.type === 'fitness_certificate' || editingDocument.type === 'medical_certificate') && (
                          <div className="space-y-2">
                            <Label>Fecha de Vencimiento</Label>
                            <Input
                              type="date"
                              value={editingDocument.metadata?.expiry_date || ''}
                              onChange={(e) => setEditingDocument({
                                ...editingDocument,
                                metadata: {...editingDocument.metadata, expiry_date: e.target.value}
                              })}
                            />
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label>Notas</Label>
                          <Textarea
                            value={editingDocument.metadata?.notes || ''}
                            onChange={(e) => setEditingDocument({
                              ...editingDocument,
                              metadata: {...editingDocument.metadata, notes: e.target.value}
                            })}
                            rows={3}
                          />
                        </div>
                      </CardContent>
                      <CardContent className="flex justify-end gap-2 pt-0">
                        <Button variant="outline" onClick={() => setEditingDocument(null)}>
                          Cancelar
                        </Button>
                        <Button onClick={handleUpdateDocument} className="bg-brand-600 hover:bg-brand-700" data-testid="edit-doc-save">
                          Guardar Cambios
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Add Document Dialog */}
                {showAddDocument && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <Card className="w-full max-w-lg mx-4">
                      <CardHeader>
                        <CardTitle>Agregar Nuevo Documento</CardTitle>
                        <CardDescription>Completa los datos del documento</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Tipo de Documento</Label>
                          <Select value={newDocument.type} onValueChange={(value) => setNewDocument({...newDocument, type: value})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="contract_representation">Contrato de Representación</SelectItem>
                              <SelectItem value="contract_club">Contrato con Club</SelectItem>
                              <SelectItem value="health_certificate">Carnet de Salud</SelectItem>
                              <SelectItem value="fitness_certificate">Carnet de Aptitud Física</SelectItem>
                              <SelectItem value="dni">DNI/Cédula</SelectItem>
                              <SelectItem value="parent_authorization">Autorización Padres</SelectItem>
                              <SelectItem value="medical_certificate">Certificado Médico</SelectItem>
                              <SelectItem value="school_id">Carnet Escolar</SelectItem>
                              <SelectItem value="other">Otro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Nombre del Documento</Label>
                          <Input
                            value={newDocument.name}
                            onChange={(e) => setNewDocument({...newDocument, name: e.target.value})}
                            placeholder="Ej: Carnet de Salud 2025"
                          />
                        </div>

                        {/* Contract with Club fields */}
                        {newDocument.type === 'contract_club' && (
                          <>
                            <div className="space-y-2">
                              <Label>Club *</Label>
                              <Input
                                value={newDocument.metadata.club_name || ''}
                                onChange={(e) => setNewDocument({...newDocument, metadata: {...newDocument.metadata, club_name: e.target.value}})}
                                placeholder="Nombre del club"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Fecha Inicio</Label>
                                <Input
                                  type="date"
                                  value={newDocument.metadata.start_date || ''}
                                  onChange={(e) => setNewDocument({...newDocument, metadata: {...newDocument.metadata, start_date: e.target.value}})}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Fecha Fin *</Label>
                                <Input
                                  type="date"
                                  value={newDocument.metadata.end_date || ''}
                                  onChange={(e) => setNewDocument({...newDocument, metadata: {...newDocument.metadata, end_date: e.target.value}})}
                                />
                              </div>
                            </div>
                          </>
                        )}

                        {/* Contract Representation fields */}
                        {newDocument.type === 'contract_representation' && (
                          <>
                            <div className="space-y-2">
                              <Label>Representante/Scout *</Label>
                              {isLegales ? (
                                // Legales: solo lectura — no puede asignar/cambiar el representante
                                <>
                                  <Input
                                    value={newDocument.metadata.representative_name || ''}
                                    disabled
                                    placeholder="Sin representante asignado"
                                    data-testid="doc-representative-readonly"
                                  />
                                  {!player?.scouted_by && (
                                    <p className="text-xs text-amber-600">
                                      Este jugador no tiene un representante asignado. Pedile al admin que asigne uno antes de cargar el contrato.
                                    </p>
                                  )}
                                  {player?.scouted_by && (
                                    <p className="text-xs text-gray-500">
                                      El representante surge del scout asignado al jugador. Solo el admin puede modificarlo.
                                    </p>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Select
                                    value={newDocument.metadata.representative_name || '__none__'}
                                    onValueChange={(v) => setNewDocument({
                                      ...newDocument,
                                      metadata: {
                                        ...newDocument.metadata,
                                        representative_name: v === '__none__' ? '' : v,
                                      },
                                    })}
                                  >
                                    <SelectTrigger data-testid="doc-representative-select">
                                      <SelectValue placeholder="Seleccioná un scout/representante" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">Sin asignar</SelectItem>
                                      {scouts.map((s) => (
                                        <SelectItem key={s.id} value={s.full_name || s.username}>
                                          {s.full_name || s.username}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {player?.scouted_by && (
                                    <p className="text-xs text-gray-500">
                                      Pre-cargado con el scout asignado al jugador. Podés cambiarlo si corresponde.
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Fecha Inicio</Label>
                                <Input
                                  type="date"
                                  value={newDocument.metadata.start_date || ''}
                                  onChange={(e) => setNewDocument({...newDocument, metadata: {...newDocument.metadata, start_date: e.target.value}})}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Fecha Fin *</Label>
                                <Input
                                  type="date"
                                  value={newDocument.metadata.end_date || ''}
                                  onChange={(e) => setNewDocument({...newDocument, metadata: {...newDocument.metadata, end_date: e.target.value}})}
                                />
                              </div>
                            </div>
                          </>
                        )}

                        {(newDocument.type === 'health_certificate' || newDocument.type === 'fitness_certificate') && (
                          <div className="space-y-2">
                            <Label>Fecha de Vencimiento (opcional)</Label>
                            <Input
                              type="date"
                              value={newDocument.metadata.expiry_date}
                              onChange={(e) => setNewDocument({...newDocument, metadata: {...newDocument.metadata, expiry_date: e.target.value}})}
                            />
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label>Archivo</Label>
                          <DocumentUpload
                            value={newDocument.file_name ? { url: newDocument.file, originalName: newDocument.file_name } : newDocument.file}
                            onChange={(val) => setNewDocument({
                              ...newDocument,
                              file: val ? val.url : '',
                              file_name: val ? val.originalName : '',
                            })}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Notas (opcional)</Label>
                          <Textarea
                            value={newDocument.metadata.notes}
                            onChange={(e) => setNewDocument({...newDocument, metadata: {...newDocument.metadata, notes: e.target.value}})}
                            placeholder="Información adicional sobre el documento"
                            rows={3}
                          />
                        </div>
                      </CardContent>
                      <CardContent className="flex justify-end gap-2 pt-0">
                        <Button variant="outline" onClick={() => setShowAddDocument(false)}>
                          Cancelar
                        </Button>
                        <Button onClick={handleAddDocument}>
                          Agregar Documento
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Investment Tab */}
          <TabsContent value="investment">
            <InvestmentTab 
              player={player} 
              onRefresh={async () => {
                const res = await axios.get(`${API}/players/${id}`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                setPlayer(res.data);
              }}
            />
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes">
            <Card>
              <CardHeader>
                <CardTitle>Notas de Seguimiento</CardTitle>
                <CardDescription>Registro histórico de observaciones</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Agregar Nueva Nota</Label>
                  <Textarea
                    placeholder="Escribe una nota sobre el jugador..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={3}
                    data-testid="new-note-textarea"
                  />
                  {/* Category selector for notes - only show if player has multiple categories */}
                  {(player.category_ids?.length > 1) && (
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-gray-600">Categoría:</Label>
                      <Select
                        value={newNoteCategory || player.category_ids?.[0]}
                        onValueChange={setNewNoteCategory}
                      >
                        <SelectTrigger className="w-[150px] h-8 text-sm">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {player.category_ids.map(catId => (
                            <SelectItem key={catId} value={catId}>{getCategoryName(catId)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button
                    onClick={handleAddNote}
                    className="bg-brand-600 hover:bg-brand-700"
                    data-testid="add-note-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Nota
                  </Button>
                </div>

                <div className="pt-4 border-t">
                  <h3 className="font-semibold text-gray-900 mb-4">Historial de Notas ({player.notes?.length || 0})</h3>
                  {player.notes && player.notes.length > 0 ? (
                    <div className="space-y-2">
                      {player.notes.slice().reverse().map((note) => (
                        <div key={note.id} className="p-3 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-brand-600 uppercase">{note.author}</span>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500 flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                {formatDate(note.date)}
                              </span>
                              {note.category_id && (
                                <>
                                  <span className="text-xs text-gray-400">•</span>
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                    {getCategoryName(note.category_id)}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-2">{note.note}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-400 text-sm">No hay notas registradas aún</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Photo Modal */}
      <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
        <DialogContent className="max-w-3xl p-0 bg-black/95 border-none">
          <button
            onClick={() => setShowPhotoModal(false)}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          {player?.photo_url && (
            <div className="flex items-center justify-center p-4">
              <img
                src={player.photo_url}
                alt={`${player.first_name} ${player.last_name}`}
                className="max-h-[80vh] max-w-full object-contain rounded-lg"
              />
            </div>
          )}
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <p className="text-white/80 text-lg font-medium">
              {player?.first_name} {player?.last_name}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

// Investment Tab Component
function InvestmentTab({ player, onRefresh }) {
  const [investments, setInvestments] = useState([]);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [materials, setMaterials] = useState([]); // Lista de materiales
  const [materialSearch, setMaterialSearch] = useState('');
  const [newInvestment, setNewInvestment] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'championes',
    description: '',
    amount: '',
    material_item_id: '',
    material_quantity: 1,
    notes: ''
  });

  useEffect(() => {
    fetchInvestments();
    fetchMaterials();
  }, [player.id]);

  const fetchInvestments = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/players/${player.id}/investments`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setInvestments(res.data.investments || []);
      setTotalInvestment(res.data.total_investment || 0);
    } catch (error) {
      console.error('Error fetching investments:', error);
    }
    setLoading(false);
  };

  const fetchMaterials = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/materials/items?limit=500`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMaterials(res.data.data || []);
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  // Filter materials with price > 0 and stock > 0
  const filteredMaterials = materials.filter(m => {
    const hasPrice = m.unit_price && m.unit_price > 0;
    const hasStock = m.quantity && m.quantity > 0;
    if (!materialSearch) return hasPrice && hasStock;
    return hasPrice && hasStock && m.name.toLowerCase().includes(materialSearch.toLowerCase());
  }).slice(0, 30);

  const selectedMaterial = materials.find(m => m.id === newInvestment.material_item_id);

  const handleAddInvestment = async () => {
    // Material-linked types can either pick from inventory or add description+amount
    const materialLinked = ['championes', 'indumentaria', 'material'].includes(newInvestment.type);
    const usingInventory = materialLinked && newInvestment.material_item_id;
    
    if (usingInventory) {
      if (!selectedMaterial) {
        toast.error('Material no válido');
        return;
      }
    } else {
      if (!newInvestment.description || !newInvestment.amount) {
        toast.error('Completa descripción y monto');
        return;
      }
    }

    try {
      const payload = {
        date: newInvestment.date,
        type: newInvestment.type,
        notes: newInvestment.notes
      };

      if (usingInventory) {
        payload.material_item_id = newInvestment.material_item_id;
        payload.material_quantity = parseInt(newInvestment.material_quantity) || 1;
      } else {
        payload.description = newInvestment.description;
        payload.amount = parseFloat(newInvestment.amount);
      }

      await axios.post(
        `${BACKEND_URL}/api/players/${player.id}/investments`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Inversión registrada');
      setShowAddModal(false);
      setNewInvestment({
        date: new Date().toISOString().split('T')[0],
        type: 'championes',
        description: '',
        amount: '',
        material_item_id: '',
        material_quantity: 1,
        notes: ''
      });
      setMaterialSearch('');
      fetchInvestments();
      fetchMaterials(); // Refresh to update stock
      onRefresh?.();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al registrar inversión';
      toast.error(errorMsg);
    }
  };

  const handleDeleteInvestment = async (investmentId) => {
    if (!window.confirm('¿Eliminar esta inversión?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/players/${player.id}/investments/${investmentId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Inversión eliminada');
      fetchInvestments();
      onRefresh?.();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getTypeLabel = (type) => {
    const types = {
      championes: 'Championes',
      indumentaria: 'Indumentaria',
      estudios_medicos: 'Estudios médicos',
      psicologo: 'Psicólogo',
      educacion: 'Educación',
      viaticos: 'Viáticos',
      canasta_alimentos: 'Canasta de alimentos',
      cursos: 'Cursos',
      adelanto: 'Adelanto',
      material: 'Material',
      otros: 'Otros'
    };
    return types[type] || type;
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      championes: 'bg-orange-100 text-orange-700',
      indumentaria: 'bg-purple-100 text-purple-700',
      estudios_medicos: 'bg-red-100 text-red-700',
      psicologo: 'bg-pink-100 text-pink-700',
      educacion: 'bg-indigo-100 text-indigo-700',
      viaticos: 'bg-amber-100 text-amber-700',
      canasta_alimentos: 'bg-emerald-100 text-emerald-700',
      cursos: 'bg-indigo-100 text-indigo-700',
      adelanto: 'bg-green-100 text-green-700',
      material: 'bg-blue-100 text-blue-700',
      otros: 'bg-gray-100 text-gray-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-brand-600" />
          <p className="text-gray-500 mt-2">Cargando inversiones...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Inversión en {player.first_name}
            </CardTitle>
            <CardDescription>Registro de gastos e inversiones realizadas</CardDescription>
          </div>
          <Button 
            onClick={() => setShowAddModal(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Agregar
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Total Investment Card */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-6 border border-green-200">
          <p className="text-sm font-medium text-green-700 mb-1">Total Invertido</p>
          <p className="text-4xl font-bold text-green-800">
            ${totalInvestment.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-green-600 mt-2">
            {investments.length} registro{investments.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Investment History Toggle */}
        <div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-green-600 transition-colors w-full justify-between p-3 bg-gray-50 rounded-lg"
          >
            <span className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Historial de Inversiones ({investments.length})
            </span>
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showHistory && (
            <div className="mt-4 space-y-3">
              {investments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No hay inversiones registradas</p>
                </div>
              ) : (
                investments.map((inv) => (
                  <div 
                    key={inv.id} 
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${
                        inv.type === 'material' ? 'bg-blue-100' : 
                        inv.type === 'adelanto' ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <DollarSign className={`w-5 h-5 ${
                          inv.type === 'material' ? 'text-blue-600' : 
                          inv.type === 'adelanto' ? 'text-green-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{inv.description}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(inv.date)}
                          <span className={`px-2 py-0.5 rounded ${getTypeBadgeColor(inv.type)}`}>
                            {getTypeLabel(inv.type)}
                          </span>
                        </div>
                        {inv.notes && (
                          <p className="text-xs text-gray-400 mt-1">{inv.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="font-bold text-lg text-gray-900">
                        ${inv.amount?.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
                      </p>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteInvestment(inv.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </CardContent>

      {/* Add Investment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b bg-green-50 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-green-800">Nueva Inversión</h2>
                <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label>Fecha</Label>
                <Input
                  type="date"
                  value={newInvestment.date}
                  onChange={(e) => setNewInvestment({...newInvestment, date: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Tipo de inversión</Label>
                <Select 
                  value={newInvestment.type}
                  onValueChange={(v) => setNewInvestment({
                    ...newInvestment, 
                    type: v, 
                    material_item_id: '', 
                    description: '',
                    amount: ''
                  })}
                >
                  <SelectTrigger className="mt-1" data-testid="investment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="championes">Championes</SelectItem>
                    <SelectItem value="indumentaria">Vestimenta / Indumentaria</SelectItem>
                    <SelectItem value="estudios_medicos">Estudios médicos</SelectItem>
                    <SelectItem value="psicologo">Psicólogo</SelectItem>
                    <SelectItem value="educacion">Profesora particular / Educación</SelectItem>
                    <SelectItem value="viaticos">Viáticos</SelectItem>
                    <SelectItem value="canasta_alimentos">Canasta de alimentos</SelectItem>
                    <SelectItem value="adelanto">Adelanto en efectivo</SelectItem>
                    <SelectItem value="otros">Otros gastos</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Material selector - optional for championes/indumentaria. If empty, user enters amount manually */}
              {['championes', 'indumentaria', 'material'].includes(newInvestment.type) && (
                <div className="border rounded-lg p-3 bg-blue-50">
                  <Label className="text-blue-700">
                    Seleccionar del inventario (opcional)
                  </Label>
                  <p className="text-xs text-blue-600 mb-2">
                    Si elegís un material del inventario se descuenta del stock. Si no, ingresá descripción y monto manualmente.
                  </p>
                  <Input
                    value={materialSearch}
                    onChange={(e) => setMaterialSearch(e.target.value)}
                    className="mt-1 mb-2"
                    placeholder="Buscar material..."
                  />
                  <div className="max-h-40 overflow-y-auto border rounded bg-white">
                    {filteredMaterials.length === 0 ? (
                      <p className="p-2 text-sm text-gray-500 text-center">
                        {materials.length === 0 ? 'No hay materiales' : 'No hay materiales con precio y stock'}
                      </p>
                    ) : (
                      filteredMaterials.map(mat => (
                        <div
                          key={mat.id}
                          className={`p-2 cursor-pointer hover:bg-blue-100 flex items-center justify-between ${
                            newInvestment.material_item_id === mat.id ? 'bg-blue-200' : ''
                          }`}
                          onClick={() => setNewInvestment({...newInvestment, material_item_id: mat.id})}
                        >
                          <span className="font-medium">{mat.name}</span>
                          <span className="text-xs text-gray-500">
                            ${mat.unit_price?.toLocaleString()} · Stock: {mat.quantity}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {selectedMaterial && (
                    <div className="mt-3 space-y-2">
                      <div>
                        <Label className="text-blue-700">Cantidad</Label>
                        <Input
                          type="number"
                          min="1"
                          max={selectedMaterial.quantity}
                          value={newInvestment.material_quantity}
                          onChange={(e) => setNewInvestment({...newInvestment, material_quantity: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <p className="text-sm text-blue-600 font-medium">
                        Total: ${((selectedMaterial.unit_price || 0) * (parseInt(newInvestment.material_quantity) || 1)).toLocaleString()}
                      </p>
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline"
                        onClick={() => setNewInvestment({...newInvestment, material_item_id: '', material_quantity: 1})}
                      >
                        Quitar selección
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Description and amount - shown when no inventory item is picked */}
              {!newInvestment.material_item_id && (
                <>
                  <div>
                    <Label>Descripción *</Label>
                    <Input
                      value={newInvestment.description}
                      onChange={(e) => setNewInvestment({...newInvestment, description: e.target.value})}
                      className="mt-1"
                      placeholder={
                        newInvestment.type === 'championes' ? 'Ej: Nike Mercurial talle 42' :
                        newInvestment.type === 'indumentaria' ? 'Ej: Buzo + camiseta de entrenamiento' :
                        newInvestment.type === 'estudios_medicos' ? 'Ej: Ecografía rodilla derecha' :
                        newInvestment.type === 'psicologo' ? 'Ej: Sesión semanal con Dr Pérez' :
                        newInvestment.type === 'educacion' ? 'Ej: Profesora particular de inglés' :
                        newInvestment.type === 'viaticos' ? 'Ej: Pasaje + hospedaje viaje a torneo' :
                        newInvestment.type === 'canasta_alimentos' ? 'Ej: Canasta mensual abril' :
                        newInvestment.type === 'adelanto' ? 'Ej: Adelanto para gastos personales' :
                        'Descripción del gasto'
                      }
                    />
                  </div>

                  <div>
                    <Label>Monto ($) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newInvestment.amount}
                      onChange={(e) => setNewInvestment({...newInvestment, amount: e.target.value})}
                      className="mt-1"
                      placeholder="0.00"
                    />
                  </div>
                </>
              )}

              <div>
                <Label>Notas adicionales</Label>
                <Textarea
                  value={newInvestment.notes}
                  onChange={(e) => setNewInvestment({...newInvestment, notes: e.target.value})}
                  className="mt-1"
                  placeholder="Observaciones..."
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowAddModal(false)} 
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleAddInvestment}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Registrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default PlayerDetail;
